import Autosuggest from 'react-autosuggest';
import Chip from '@material-ui/core/Chip';
import compose from 'recompose/compose';
import DialogContainer from 'features/Dialog';
import has from 'lodash.has';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import ProductFilters from 'models/ProductFilters';
import PropTypes from 'prop-types';
import React from 'react';
import SearchButton from '@material-ui/icons/Search';
import TextField from '@material-ui/core/TextField';
import toJS from 'shared/components/toJS';
import { actions as navBarActions } from 'redux/domain/NavBar';
import { actions as notificationActions, SnackbarSettings } from 'redux/domain/Notification';
import { actions as productFilterActions } from 'redux/domain/ProductFilter';
import { actions as productSearchActions } from 'redux/domain/ProductSearch';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { getCancelTokenSource } from 'api/Helpers';
import { PRODUCTS } from 'redux/domain/ProductSearch/constants';
import { Scrollbars } from 'react-custom-scrollbars';
import { withStyles } from '@material-ui/core/styles';
import withRouter from '../../../shared/utils/WithRouter';

const styles = theme => ({
	autoSuggestContainer: {
		display: 'inline',
		position: 'absolute',
		padding: 12,
		left: 24,
	},
	autoSuggestWidth: {
		marginTop: '0.5em',
		width: 'auto',
		[theme.breakpoints.down('xs')]: {
			width: '50vw',
		},
	},
	autoSuggestMenuItem: {
		maxWidth: window.innerWidth,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		[theme.breakpoints.down('xs')]: {
			fontSize: 13,
			maxWidth: '12rem',
		},
	},
	chipLabel: {
		maxWidth: '8rem',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		[theme.breakpoints.up('sm')]: {
			maxWidth: '16rem',
		},
	},
	chipRoot: {
		backgroundColor: theme.palette.secondary.main,
		color: theme.palette.getContrastText(theme.palette.secondary.main),
		cursor: 'pointer',
		fontSize: 14,
	},
	clickable: {
		'&:hover, &:focus': {
			backgroundColor: theme.palette.secondary.main,
		},
		'&:active': {
			backgroundColor: theme.palette.secondary.main,
			boxShadow: theme.shadows[1],
		},
	},
	container: {
		flexGrow: 1,
		height: 50,
		position: 'relative',
	},
	deleteIconStyle: {
		fill: theme.palette.common.white,
	},
	iconButtonStyle: {
		backgroundColor: 'transparent',
		color: 'inherit',
		left: '-12px',
		position: 'relative',
		top: '12px',
		verticalAlign: 'baseline', // prevents the button from jumping
	},
	input: {
		[theme.breakpoints.down('sm')]: {
			width: 128,
		},
		[theme.breakpoints.up('sm')]: {
			width: 192,
		},
		[theme.breakpoints.up('lg')]: {
			width: 256,
		},
	},
	suggestion: {
		display: 'block',
	},
	suggestionsList: {
		listStyleType: 'none',
		margin: 0,
		padding: 0,
	},
});

class SearchFieldContainer extends React.Component {
	constructor() {
		super();

		this.state = {
			backupText: '',
			promptMessage: '',
			searchText: '',
			searchVisible: false,
			suggestions: [],
		};
		this.cancelTokenSource = getCancelTokenSource();
		this.lastAutoCompleteOption = { text: 'View All' };
	}

	/**
	 * Required for Autosuggest. Returns a suggestion's text value.
	 * @param {object} suggestion suggestion to pull text out
	 * @returns String value of the suggestion for display
	 */
	getSuggestionValue = suggestion => suggestion.text;

	/**
	 * Needed for Autosuggest.
	 * Gets an array of 10 possible suggestions if the value is longer than three characters
	 * @param {string} value input value to search
	 * @returns An array of 10 suggestions to render to the Autosugget dropdown
	 */
	getSuggestions = value => {
		let dataSource = [];
		if (value && value.length >= 3) {
			const { filters } = this.props;
			const products = this.getItemsFromStore(filters.term);
			dataSource = products.map(product => ({
				sku: product.sku,
				text: product.name,
				value: product.id,
			}));
			// only show 10 items
			dataSource.splice(10);
			// add the View All option
			if (dataSource.length > 1) {
				dataSource.push(this.lastAutoCompleteOption);
			}
		}
		return dataSource;
	};

	/**
	 * Return a list of associated products from the store if available.
	 * @param {string} term filters.term that was given to search
	 * @returns An array of products or an empty array if they have not be fetched yet
	 */
	getItemsFromStore(term) {
		const prefetchedItems = this.props[PRODUCTS];
		return has(prefetchedItems, term, PRODUCTS) ? prefetchedItems[term][PRODUCTS] : [];
	}

	/**
	 * Determines if the chip needs to be displayed on blur of TextField
	 */
	focusFunction = () => {
		const { showChip } = this.props;
		if (this.state.searchText.length >= 3) {
			showChip(this.state.searchText);
			this.setState({
				searchText: '',
				searchVisible: false,
			});
		}
	};

	/**
	 * Handles the input given by the Autosuggest component.
	 * @param {object} event event object
	 * @param {object} newValue new text to update state and then suggestions array
	 */
	handleInput = (event, { newValue }) => {
		if (newValue !== this.lastAutoCompleteOption.text) {
			const {
				disableNotification,
				filters,
				getFilteredProducts,
				receiveFilters,
				showNotification,
			} = this.props;
			const adjustFilterTerm = Object.assign(new ProductFilters(), filters);

			// only start searching once 3 characters have been entered, otherwise we are bogging down resources searching by one letter
			if (newValue.length >= 3)
			{
				adjustFilterTerm.term = newValue;
				receiveFilters(adjustFilterTerm);
				// retrieve products to be used for the autocomplete list here
				// wait for the callback then render the suggestion list based on value
				// .then promise needed to render properly
				getFilteredProducts(this.cancelTokenSource.token, adjustFilterTerm)
					.then(() => {
						this.handleSuggestionsFetchRequested(newValue);
					})
					.catch(error => {
						const settings = new SnackbarSettings(true, error.message);
						// hide after 5 seconds
						settings.autoHideDuration = 5000;
						settings.onRequestClose = disableNotification;
						showNotification(settings, []);
					});
			}

			this.setState({ backupText: newValue, searchText: newValue });
		}
	};

	/**
	 * Push to the specific product page if given.
	 * @param {Number} id id of product
	 * @param {string} sku sku value
	 */
	handleNavToProd = (id, sku) => {
		this.props.router.navigate(`/product/${id}`, { 
			state: { 
				sku: sku 
			} 
		});
	};

	/**
	 * Handle an Autosuggest selection of either a search or a specific product.
	 * Depending on the size of the screen, this function will shorten the search text
	 * to fit the chip neatly.
	 * @param {object} event event object
	 * @param {object|string} suggestion selected text or product object
	 */
	handleSelect(event, { suggestion }) {
		// Upon clicking a MenuItem selection, the handleSelect re-renders as part of the Autosuggest
		// component. It will send "undefined" to the handleSelect in an attempt to render suggestions.
		// If it's undefined, we just ignore this function.
		if (suggestion !== undefined) {
			const { filters, receiveFilters, showChip } = this.props;

			// If suggestion is an object, it means we are going directly to a product page.
			// In that case, we do not need to update the filter search term.
			if (filters.term === '' && typeof suggestion !== 'object') {
				const adjustFilterTerm = Object.assign(new ProductFilters(), filters);
				adjustFilterTerm.term = suggestion;
				receiveFilters(adjustFilterTerm);
			}

			if (suggestion.text === 'View All' || suggestion === 'View All') {
				showChip(this.state.backupText);

				this.setState({
					searchText: '',
					searchVisible: false,
				});
				this.props.router.navigate('/category/search');
			} else if (typeof suggestion === 'object') {
				this.setState({ searchText: suggestion.text, searchVisible: false });

				showChip(suggestion);

				this.handleNavToProd(suggestion.value, suggestion.sku);
			} else if (typeof suggestion === 'string' && suggestion.length < 3) {
				// If the text is less than three characters, should not go to search page
				this.setState({ promptMessage: 'Search text must be at least three characters.' });
			} else {
				this.setState({ searchText: suggestion, searchVisible: false });
				showChip(suggestion);
				this.props.router.navigate('/category/search');
			}
		}
	}

	/**
	 * Required for Autosuggest. Updates the state of suggestions when not called with an object.
	 * @param {string|object} value search term; only updates when string
	 */
	handleSuggestionsFetchRequested = value => {
		if (typeof value !== 'object') {
			this.setState({
				suggestions: this.getSuggestions(value),
			});
		}
	};

	/**
	 * Determines if it can toggle the Searchbar TextField on or off.
	 */
	toggleSearchBar = () => {
		const { isChipVisible } = this.props;
		const searchBarVisible = this.state.searchVisible;
		if (searchBarVisible) {
			this.setState({ searchText: '', searchVisible: false, suggestions: [] });
		} else if (!isChipVisible) {
			this.setState({ searchText: '', searchVisible: true, suggestions: [] });
		} else {
			this.setState({
				promptMessage: 'Please delete the chip before typing in a new search.',
			});
		}
	};

	/**
	 * Deletes the chip when called and updates filters
	 */
	removeChip = () => {
		const { deleteChip, filters, receiveFilters } = this.props;
		deleteChip();
		// remove the filter term
		const adjustFilterTerm = Object.assign(new ProductFilters(), filters);
		adjustFilterTerm.term = '';
		receiveFilters(adjustFilterTerm);
	};

	/**
	 * Required for Autosuggest. Renders a TextField to handle input.
	 * @param {object} inputProps object passed with values for input stying
	 * @returns A TextField MUI component for input
	 */
	renderInput = inputProps => {
		const { classes, ref, ...other } = inputProps;
		return (
			<TextField
				autoFocus
				InputProps={{
					classes: {
						input: classes.input,
					},
					inputRef: ref,
					...other,
				}}
				onKeyPress={event =>
					event.key === 'Enter'
						? this.handleSelect({}, { suggestion: this.state.searchText })
						: null
				}
			/>
		);
	};

	/**
	 * Required for Autosuggest. Renders the suggestions into MenuItems for the dropdown menu
	 * @param {object} suggestion suggestion value to make into MenuItem
	 * @returns The suggestion listed as a MUI MenuItem
	 */
	renderSuggestion = suggestion => (
		<MenuItem
			component="div"
			key={String(suggestion.text)}
			onClick={() => this.handleSelect({}, { suggestion })}
		>
			<div className={this.props.classes.autoSuggestMenuItem}>{suggestion.text}</div>
		</MenuItem>
	);

	/**
	 * Required for Autosuggest. Renders the suggestions container to display
	 * @param {object} options suggestions values that are MenuItems for display
	 * @returns A MUI Paper component with suggestions as MenuItems
	 */
	renderSuggestionsContainer = options => {
		const { children, containerProps } = options;
		if (children) {
			// If the screen height is small, make the paper the length of the screen minus the height from the navBar
			return (
				<Paper
					className={this.props.classes.autoSuggestWidth}
					{...containerProps}
					elevation={20}
					square
				>
					<Scrollbars autoHeight autoHeightMax="100vh - 64px">
						<div>{children}</div>
					</Scrollbars>
				</Paper>
			);
		}
		return null;
	};

	render() {
		const { chipText, classes, isChipVisible } = this.props;

		// put a span around the SearchButton component because it helped with positioning in the navBar
		return (
			<div>
				<IconButton className={classes.iconButtonStyle} onClick={() => this.toggleSearchBar()}>
					<span>
						<SearchButton />
					</span>
				</IconButton>
				{this.state.searchVisible ? (
					<div className={classes.autoSuggestContainer}>
						<Autosuggest
							alwaysRenderSuggestions
							getSuggestionValue={this.getSuggestionValue}
							inputProps={{
								classes,
								onBlur: this.focusFunction,
								onChange: this.handleInput,
								placeholder: 'Search',
								value: this.state.searchText,
							}}
							onSuggestionsFetchRequested={this.handleSuggestionsFetchRequested}
							onSuggestionSelected={suggestion => this.handleSelect({}, suggestion)}
							renderInputComponent={this.renderInput}
							renderSuggestion={this.renderSuggestion}
							renderSuggestionsContainer={this.renderSuggestionsContainer}
							suggestions={this.state.suggestions}
							theme={{
								container: classes.container,
								suggestion: classes.suggestion,
								suggestionsList: classes.suggestionsList,
							}}
						/>
					</div>
				) : null}
				{isChipVisible ? (
					<div style={{ marginLeft: 32, marginTop: -30 }}>
						<Chip
							classes={{
								clickable: classes.clickable,
								deleteIcon: classes.deleteIconStyle,
								root: classes.chipRoot,
							}}
							clickable
							label={
								<span className={classes.chipLabel}>
									{chipText.text === undefined ? chipText : chipText.text}
								</span>
							}
							onClick={() => this.handleSelect({}, { suggestion: chipText })}
							onDelete={this.removeChip}
						/>
					</div>
				) : null}
				{this.state.promptMessage ? (
					<DialogContainer
						buttonLabel="OK"
						message={this.state.promptMessage}
						onClose={() => this.setState({ promptMessage: undefined })}
					/>
				) : null}
			</div>
		);
	}
}

SearchFieldContainer.propTypes = {
	chipText: PropTypes.oneOfType([PropTypes.string, PropTypes.objectOf(PropTypes.any)]).isRequired,
	classes: PropTypes.shape({
		autoSuggestMenuItem: PropTypes.string,
		autoSuggestWidth: PropTypes.string,
		chipLabel: PropTypes.string,
		chipRoot: PropTypes.string,
		clickable: PropTypes.string,
		container: PropTypes.string,
		deleteIconStyle: PropTypes.string,
		iconButtonStyle: PropTypes.string,
		input: PropTypes.string,
		suggestion: PropTypes.string,
		suggestionsList: PropTypes.string,
	}).isRequired,
	deleteChip: PropTypes.func.isRequired,
	disableNotification: PropTypes.func.isRequired,
	filters: PropTypes.instanceOf(ProductFilters).isRequired,
	getFilteredProducts: PropTypes.func.isRequired,
	isChipVisible: PropTypes.bool.isRequired,
	receiveFilters: PropTypes.func.isRequired,
	showChip: PropTypes.func.isRequired,
	showNotification: PropTypes.func.isRequired,
};

const mapStateToProps = state => {
	const chipText = state.navbar.get('chipText');
	const filters = state.productFilter.get('filters');
	const isChipVisible = state.navbar.get('isChipVisible');
	const products = state.productSearch.get('products');
	const searchResultsHeader = state.productSearch.get('searchResultsHeader');
	return {
		chipText,
		filters,
		isChipVisible,
		products,
		searchResultsHeader,
	};
};

const mapDispatchToProps = dispatch =>
	bindActionCreators(
		{ ...navBarActions, ...notificationActions, ...productFilterActions, ...productSearchActions },
		dispatch,
	);

// for testing purposes
export { SearchFieldContainer as SearchFieldContainerClass };

// connect returns a function that accepts a component
export default withRouter(compose(
	connect(mapStateToProps, mapDispatchToProps),
	withStyles(styles, { name: 'SearchFieldContainer' }),
)(toJS(SearchFieldContainer)));
